import React, { useMemo, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { ExternalLink, Heart, MessageCircle, Play, Share2 } from "lucide-react";

/**
 * Per-creator side drawer — clicking a creator row on the Insights → Creators
 * table opens this. Shows:
 *   • Header: avatar, handle, follower count, matched/collab totals.
 *   • Content-type toggle: All / Reels / Static / Carousel / Stories.
 *   • Per-day post-frequency bar chart (respects the toggle).
 *   • Post list with thumbnail, type pill, date, metrics, and a link that
 *     opens the actual Instagram post in a new tab.
 *
 * Zero API calls — filters/aggregates the `posts` array already loaded by
 * LatestRunSummary (i.e. respects the report's active date filter).
 */
export function CreatorDrawer({ open, onClose, creator, posts = [], storyRows = [] }) {
  const [kind, setKind] = useState("all");
  const handle = creator?.handle;

  const own = useMemo(() => {
    if (!handle) return [];
    return (posts || []).filter(
      (p) => p.handle === handle || (p.original_owner || "").toLowerCase() === handle.toLowerCase()
    );
  }, [posts, handle]);

  // Story-timeline slice for this creator. `storyRows` is the aggregated
  // day-by-day count (report_story_counts), NOT individual post rows —
  // so the drawer's Stories tab shows dates + totals + live/highlights.
  const storyDays = useMemo(() => {
    if (!handle) return [];
    return (storyRows || [])
      .filter((r) => r.handle === handle)
      // Zero-activity days (0 live AND 0 highlights) are noise — hidden.
      .filter((r) => (r.live || 0) > 0 || (r.highlights || 0) > 0)
      .sort((a, b) => (a.run_date < b.run_date ? 1 : -1));
  }, [storyRows, handle]);
  // Totals count LIVE stories only — highlights are pinned fixtures, never
  // counted in totals (user spec).
  const storyDaysTotal = storyDays.reduce((n, r) => n + (r.live || 0), 0);

  const filtered = useMemo(() => {
    if (kind === "all") return own;
    if (kind === "reels") return own.filter((p) => p.post_type === "Reel" || p.post_type === "Video");
    if (kind === "static") return own.filter((p) => p.post_type === "Static");
    if (kind === "carousel") return own.filter((p) => (p.post_type || "").startsWith("Carousel"));
    if (kind === "stories") return own.filter((p) => (p.post_type || "").startsWith("Story"));
    return own;
  }, [own, kind]);

  const chartData = useMemo(() => {
    const bucket = new Map();
    for (const p of filtered) {
      if (!p.post_date) continue;
      const d = String(p.post_date).slice(0, 10);
      bucket.set(d, (bucket.get(d) || 0) + 1);
    }
    return [...bucket.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, count]) => ({ date, count }));
  }, [filtered]);

  const stats = useMemo(() => {
    const s = { views: 0, likes: 0, comments: 0, shares: 0 };
    for (const p of filtered) {
      s.views += Number(p.video_views || p.video_plays || 0);
      s.likes += Number(p.likes || 0);
      s.comments += Number(p.comments || 0);
      if (p.shares != null) s.shares += Number(p.shares || 0);
    }
    return s;
  }, [filtered]);

  const compact = (n) => {
    if (!n) return "0";
    if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
    if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
    return String(n);
  };
  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });

  const kinds = [
    { id: "all",      label: "All",       count: own.length },
    { id: "reels",    label: "Reels",     count: own.filter((p) => p.post_type === "Reel" || p.post_type === "Video").length },
    { id: "static",   label: "Static",    count: own.filter((p) => p.post_type === "Static").length },
    { id: "carousel", label: "Carousel",  count: own.filter((p) => (p.post_type || "").startsWith("Carousel")).length },
    { id: "stories",  label: "Stories",   count: storyDaysTotal || own.filter((p) => (p.post_type || "").startsWith("Story")).length },
  ];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-white border-l border-slate-200 text-slate-900 p-0 overflow-y-auto"
        data-testid="creator-drawer"
      >
        {creator && (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-start gap-3">
                {creator.profile_pic_url ? (
                  <img
                    src={creator.profile_pic_url}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-14 h-14 rounded-full object-cover border border-slate-200"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center text-lg font-bold text-white">
                    {(creator.handle || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-lg font-semibold tracking-tight text-slate-900">
                    {creator.owner_full_name || `@${creator.handle}`}
                  </SheetTitle>
                  <SheetDescription className="text-xs font-mono text-slate-400 mt-0.5">
                    <a
                      href={creator.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-pink-600 inline-flex items-center gap-1"
                    >
                      @{creator.handle} <ExternalLink size={10} />
                    </a>
                    <span className="ml-3 text-slate-500">{compact(creator.followers)} followers</span>
                  </SheetDescription>
                </div>
              </div>

              {/* Aggregate stats for the current filter */}
              <div className="grid grid-cols-4 gap-2 mt-4">
                <MiniStat label="Views" value={compact(stats.views)} icon={Play} />
                <MiniStat label="Likes" value={compact(stats.likes)} icon={Heart} />
                <MiniStat label="Comments" value={compact(stats.comments)} icon={MessageCircle} />
                <MiniStat label="Shares" value={compact(stats.shares)} icon={Share2} />
              </div>
            </SheetHeader>

            {/* Kind toggle */}
            <div className="px-6 pt-4 pb-2">
              <div
                className="flex items-center gap-1 p-0.5 bg-slate-100 border border-slate-200 rounded-lg w-fit"
                data-testid="creator-drawer-kind-toggle"
              >
                {kinds.map((k) => (
                  <button
                    key={k.id}
                    onClick={() => setKind(k.id)}
                    data-testid={`creator-drawer-kind-${k.id}`}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                      kind === k.id
                        ? "bg-white text-slate-900 shadow-sm border border-slate-200/80"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {k.label}
                    <span className="ml-1.5 text-[10px] font-mono text-slate-400 tabular-nums">
                      {k.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Frequency chart */}
            {chartData.length > 0 && (
              <div className="px-6 pb-4" data-testid="creator-drawer-chart">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2">
                  Activity over time
                </div>
                <div className="h-32 rounded-lg border border-slate-200/80 bg-slate-50/50 px-2 pt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                      <CartesianGrid vertical={false} stroke="#F1F5F9" />
                      <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d).slice(0, 6)}
                             tick={{ fontSize: 9, fill: "#94A3B8" }}
                             axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={30} />
                      <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false}
                             tickLine={false} allowDecimals={false} width={22} />
                      <Tooltip
                        cursor={{ fill: "rgba(236, 72, 153, 0.06)" }}
                        content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <div className="rounded-lg bg-white border border-slate-200 shadow-lg px-2.5 py-1.5 text-[11px]">
                              <div className="font-semibold text-slate-900">{fmtDate(label)}</div>
                              <div className="text-slate-600 tabular-nums">{payload[0].value} items</div>
                            </div>
                          ) : null
                        }
                      />
                      <Bar dataKey="count" fill="#F9A8D4" radius={[3, 3, 0, 0]} maxBarSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Story timeline (aggregated day counts) — always visible on
                the Stories tab, even when there are no post-typed rows. */}
            {kind === "stories" && storyDays.length > 0 && (
              <div className="px-6 pb-4" data-testid="creator-drawer-story-timeline">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2 flex items-center justify-between">
                  <span>Story timeline · {storyDaysTotal} live stories</span>
                  <span className="text-slate-300">highlights not counted in totals</span>
                </div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50/70 text-[10px] uppercase tracking-wider font-mono text-slate-400">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Date</th>
                        <th className="text-right px-3 py-2 font-medium">Stories (Live)</th>
                        <th className="text-right px-3 py-2 font-medium">Highlights</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storyDays.map((r) => (
                        <tr key={r.run_date} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 font-mono text-slate-500 tabular-nums">{r.run_date}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold text-slate-900 tabular-nums">{r.live || 0}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-slate-500 tabular-nums">{r.highlights || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Post list */}
            <div className="px-6 pb-8" data-testid="creator-drawer-list">
              {filtered.length === 0 && !(kind === "stories" && storyDays.length > 0) ? (
                <div className="text-center text-xs font-mono text-slate-400 py-10 border border-dashed border-slate-200 rounded-lg">
                  No items match this filter for @{creator.handle}.
                </div>
              ) : filtered.length === 0 ? null : (
                <div className="space-y-2">
                  {filtered
                    .slice()
                    .sort((a, b) => (a.post_date < b.post_date ? 1 : -1))
                    .map((p) => (
                      <PostRow key={p.shortcode || p.post_url} post={p} compact={compact} fmtDate={fmtDate} />
                    ))}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const MiniStat = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2">
    <div className="flex items-center justify-between text-slate-400 mb-0.5">
      <span className="text-[9px] font-mono uppercase tracking-wider">{label}</span>
      <Icon size={11} className="text-pink-500 opacity-80" />
    </div>
    <div className="text-base font-semibold text-slate-900 tabular-nums">{value}</div>
  </div>
);

const KIND_STYLES = {
  Reel: "bg-pink-50 text-pink-700 border-pink-200",
  Video: "bg-pink-50 text-pink-700 border-pink-200",
  "Carousel-Video": "bg-violet-50 text-violet-700 border-violet-200",
  Carousel: "bg-violet-50 text-violet-700 border-violet-200",
  Static: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Story-Image": "bg-amber-50 text-amber-700 border-amber-200",
  "Story-Video": "bg-amber-50 text-amber-700 border-amber-200",
};

const PostRow = ({ post, compact, fmtDate }) => {
  const kindClass = KIND_STYLES[post.post_type] || "bg-slate-100 text-slate-600 border-slate-200";
  const views = Number(post.video_views || post.video_plays || 0);
  return (
    <a
      href={post.post_url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors p-2.5 group"
      data-testid="creator-drawer-post-row"
    >
      {post.thumbnail ? (
        <img
          src={post.thumbnail}
          alt=""
          referrerPolicy="no-referrer"
          className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${kindClass}`}>
            {post.post_type}
          </span>
          {post.post_date && (
            <span className="text-[10px] text-slate-400 font-mono">{fmtDate(post.post_date)}</span>
          )}
          {post.is_collab && (
            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200">
              Collab
            </span>
          )}
        </div>
        {post.caption && (
          <p className="text-xs text-slate-700 leading-snug line-clamp-2">{post.caption}</p>
        )}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500 font-mono tabular-nums">
          <span className="flex items-center gap-1"><Heart size={11} className="text-pink-500" /> {compact(post.likes || 0)}</span>
          <span className="flex items-center gap-1"><MessageCircle size={11} className="text-sky-500" /> {compact(post.comments || 0)}</span>
          {views > 0 && (
            <span className="flex items-center gap-1"><Play size={11} className="text-violet-500" /> {compact(views)}</span>
          )}
          {post.shares != null && (
            <span className="flex items-center gap-1"><Share2 size={11} className="text-emerald-500" /> {compact(post.shares || 0)}</span>
          )}
        </div>
      </div>
      <ExternalLink size={12} className="text-slate-300 group-hover:text-pink-500 shrink-0 mt-1" />
    </a>
  );
};
